// src/lib/portfolioCalc.ts: portfolio totals and weighted YTD return for the
// allocator's private tracker.
import { supabase } from '../services/supabase';

export interface Investment {
  id: string;
  manager_firm_id: string | null;
  manager_name: string;
  fund_id: string | null;
  fund_name: string;
  amount_invested: number;
  currency: string;
  investment_date: string;
  current_value: number | null;
  notes: string | null;
}

export interface PortfolioSummary {
  /** Weighted return across the positions that have performance data. */
  ytdReturn: number | null;
  /** Sum of every active position, as entered. */
  totalInvested: number;
  /** Sum of the positions the return is actually measured over. */
  coveredInvested: number;
  /** Per-fund YTD, so a row can show its own number without refetching. */
  fundYtd: Record<string, number>;
  /** Currencies present, so mixed-currency totals can be flagged. */
  currencies: string[];
}

/**
 * Weighted YTD across the portfolio.
 *
 * The weighting deliberately uses the covered subtotal rather than the whole
 * portfolio. Weighting by the full total would dilute the figure toward zero
 * for every position with no linked fund: $10m at +10% alongside $10m of
 * untracked manual entries would report +5%, implying the whole book returned
 * 5% when only half of it is measured at all. Reporting the return over what is
 * measured, plus the covered proportion, is the honest version. Callers can
 * show coverage from coveredInvested / totalInvested.
 */
export async function calculatePortfolioYTD(
  investments: Investment[],
): Promise<PortfolioSummary> {
  const currentYear = new Date().getFullYear();
  const totalInvested = investments.reduce((sum, inv) => sum + (Number(inv.amount_invested) || 0), 0);
  const currencies = [...new Set(investments.map(i => i.currency || 'USD'))];

  const empty: PortfolioSummary = {
    ytdReturn: null, totalInvested, coveredInvested: 0, fundYtd: {}, currencies,
  };
  if (totalInvested === 0) return { ...empty, totalInvested: 0 };

  // One round trip for every linked fund rather than one per investment.
  const fundIds = [...new Set(investments.map(i => i.fund_id).filter((id): id is string => !!id))];
  if (fundIds.length === 0) return empty;

  const { data: perf, error } = await supabase
    .from('fund_performance')
    .select('fund_id, month, year, return_pct')
    .in('fund_id', fundIds)
    .eq('year', currentYear)
    .order('month', { ascending: true });

  if (error || !perf || perf.length === 0) return empty;

  const byFund: Record<string, { month: number; return_pct: number }[]> = {};
  (perf as any[]).forEach(p => {
    if (!byFund[p.fund_id]) byFund[p.fund_id] = [];
    byFund[p.fund_id].push({ month: p.month, return_pct: p.return_pct });
  });

  // Compound each fund's monthly returns into a YTD figure.
  const fundYtd: Record<string, number> = {};
  Object.entries(byFund).forEach(([fundId, rows]) => {
    const compound = rows.reduce((acc, r) => acc * (1 + (Number(r.return_pct) || 0) / 100), 1);
    fundYtd[fundId] = (compound - 1) * 100;
  });

  const covered = investments.filter(i => i.fund_id && fundYtd[i.fund_id] !== undefined);
  const coveredInvested = covered.reduce((sum, i) => sum + (Number(i.amount_invested) || 0), 0);
  if (coveredInvested === 0) return { ...empty, fundYtd };

  const weightedReturn = covered.reduce(
    (acc, i) => acc + (Number(i.amount_invested) / coveredInvested) * fundYtd[i.fund_id as string],
    0,
  );

  return { ytdReturn: weightedReturn, totalInvested, coveredInvested, fundYtd, currencies };
}

export function formatReturn(pct: number | null): string {
  if (pct === null || pct === undefined || Number.isNaN(pct)) return '--';
  const sign = pct >= 0 ? '+' : '';
  return `${sign}${pct.toFixed(2)}%`;
}

export function formatAUM(amount: number, currency: string = 'USD'): string {
  const n = Number(amount) || 0;
  if (n >= 1_000_000_000) return `${currency} ${(n / 1_000_000_000).toFixed(1)}bn`;
  if (n >= 1_000_000) return `${currency} ${(n / 1_000_000).toFixed(1)}m`;
  if (n >= 1_000) return `${currency} ${(n / 1_000).toFixed(0)}k`;
  return `${currency} ${n.toLocaleString()}`;
}

export const CURRENCIES = ['USD', 'GBP', 'EUR', 'CHF', 'SGD'];

/**
 * Totals are a plain sum of the entered amounts. There is no FX rate source in
 * the platform, so a portfolio holding more than one currency cannot be
 * converted, and the UI labels the total as mixed rather than quoting a
 * currency it has not actually converted to.
 */
export function totalLabel(total: number, currencies: string[]): string {
  if (currencies.length <= 1) return formatAUM(total, currencies[0] || 'USD');
  return `${formatAUM(total, '')} (mixed)`.trim();
}
